<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { ChartColumn } from "lucide-vue-next";
  import { Card } from "@/components/ui/card";
  import { Badge } from "@/components/ui/badge";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Progress } from "@/components/ui/progress";
  import { useBidderTargetsStore } from "@stores/bidderTargets";
  import { relativeTimeFrom } from "@/utils/time";
  import { useBudgetStore } from "@stores/budget";

  const bidderTargetsStore = useBidderTargetsStore();
  const { bidderTargets, bidderStatus } = storeToRefs(bidderTargetsStore);
  const { pProgressGlobal, pProgress, pTimeGlobal, pMarginGlobal } = bidderTargetsStore;

  const { bidderFail } = storeToRefs(useBudgetStore());

  const statusCode = computed(() => bidderStatus.value?.status);

  const statusLabel = computed(() => {
    const code = statusCode.value;
    if (!code) return "Idle";
    if (code.startsWith("S1") || code.startsWith("S2") || code.startsWith("S3") || code.startsWith("S4")) {
      return "Success";
    }
    if (code === "S5_no_target_max_no_xquota_min") return "Warning";
    return "Failed";
  });

  const STATUS_VARIANTS = {
    Warning: "warning",
    Failed: "danger",
  };

  const statusVariant = computed(() => STATUS_VARIANTS[statusLabel.value] || "outline");

  const statusBadgeClass = computed(() => {
    switch (statusLabel.value) {
      case "Success":
        return "bg-status-success-soft text-status-success-strong border-status-success-border";
      case "Warning":
      case "Failed":
        return "";
      default:
        return "bg-muted text-muted-foreground";
    }
  });

  const relativeTime = computed(() =>
    bidderStatus.value?.created_at ? relativeTimeFrom(bidderStatus.value.created_at) : "--",
  );

  const progressPct = computed(() => (Number(pProgressGlobal()) * 100).toFixed(0));
  const marginPct = computed(() => (Number(pMarginGlobal()) * 100).toFixed(0));

  const PROGRESS_GOOD_THRESHOLD = 80;
  const PROGRESS_WARNING_THRESHOLD = 50;
  const progressBarClass = computed(() => {
    const v = Number(progressPct.value);
    if (v >= PROGRESS_GOOD_THRESHOLD) {
      return "bg-status-success-soft [&>[data-slot=progress-indicator]]:bg-status-success";
    }
    if (v >= PROGRESS_WARNING_THRESHOLD) {
      return "bg-status-warning-soft [&>[data-slot=progress-indicator]]:bg-status-warning";
    }
    return "bg-status-error-soft [&>[data-slot=progress-indicator]]:bg-status-error";
  });
</script>

<template>
  <Card class="p-5">
    <div class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bidder</div>

    <div class="mt-3 space-y-2 text-sm">
      <div class="flex items-center justify-between">
        <span class="text-muted-foreground">Status</span>
        <Badge
          :variant="statusVariant"
          :class="statusBadgeClass">
          {{ statusLabel }}
        </Badge>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-muted-foreground">Last run</span>
        <span class="font-medium">{{ relativeTime }}</span>
      </div>
    </div>

    <div class="mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Predictions</div>

    <div
      v-if="bidderFail"
      class="mt-2 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
      Error bidder run, can't display predictions.
    </div>

    <div
      v-else
      class="mt-3 space-y-3 text-sm">
      <Tooltip :disabled="!bidderTargets || bidderTargets.length === 0">
        <template #content>
          <div
            v-for="t in bidderTargets"
            :key="`progress-${t.target_name}`">
            {{ t.target_name }}: <strong>{{ pProgress(t).toFixed(1) }} %</strong>
          </div>
        </template>
        <div class="space-y-1.5">
          <div class="flex items-center justify-between">
            <span class="text-muted-foreground">pProgress</span>
            <span class="font-medium tabular-nums">{{ progressPct }}%</span>
          </div>
          <Progress
            :model-value="Math.min(Number(progressPct), 100)"
            :class="['h-1.5', progressBarClass]" />
        </div>
      </Tooltip>
      <div class="flex items-center justify-between">
        <span class="text-muted-foreground">pTime</span>
        <Tooltip :disabled="!bidderTargets || bidderTargets.length === 0">
          <template #content>
            <div
              v-for="t in bidderTargets"
              :key="`time-${t.target_name}`">
              {{ t.target_name }}: <strong>{{ t.b_pdays?.toFixed(1) ?? "--" }} days</strong>
            </div>
          </template>
          <span class="font-medium tabular-nums">{{ pTimeGlobal() }} days</span>
        </Tooltip>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-muted-foreground">pMargin</span>
        <span class="font-medium tabular-nums">{{ marginPct }}%</span>
      </div>
    </div>

    <a
      href="https://docs.google.com/spreadsheets/d/1ymtIwT8LzmRz7JJdzHN9OydwzLw8xOMd8uMIP8Y27ns/edit#gid=242502749"
      target="_blank"
      rel="noopener noreferrer"
      class="mt-5 inline-flex items-center gap-1.5 text-sm text-status-success-strong">
      <ChartColumn class="h-4 w-4" />
      Report
    </a>
  </Card>
</template>
